import { Grid, Typography } from '@mui/material';
import { ApprovalButton } from '@tassat/button';
import {
    AssetTypes,
    ClientWalletRoleEnum,
    DeclineReason,
    // DeclineReasonCodeEnum,
    DeclineReasons,
    TransactionStatusCode
    // TransactionTypeChar,
    // AdminRoleId,
} from '@tassat/common-definitions';
import { formatEpochDate } from '@tassat/text/src/helpers/helpers';
import React, { useMemo } from 'react';

import { useAppSelector } from 'Reducers/store/hooks';
import { getUser } from 'Selectors/common';
import { PledgeActionMenu } from 'SharedComponents/menu/index';
import { PledgeContextWorkflowContext } from 'SharedComponents/modals/pledgeDetailsModal/pledgeContextWorkflow';
import type { ModalScreen } from 'SharedComponents/modals/pledgeDetailsModal/pledgeDetailsModal';
import { muiStyles } from 'SharedComponents/transactionDetails/styles';

import { PledgeHeaderSectionDetails } from './pledgeHeaderSectionDetails/pledgeHeaderSectionDetails';
import { PledgeDetailsProps, PledgeMenuOption } from './types';

export default function PledgeDetails(props: PledgeDetailsProps) {
    const {
        selectedPledge,
        currentUser,
        currentRole,
        userPledgorAccount,
        userPledgeeAccount,
        killSwitch,
        changeModalScreen,
        asset
    } = props;
    const classes = muiStyles(props);
    const { pledgeContextWorkflowState, dispatchPledgeContextWorkflowAction } = React.useContext(PledgeContextWorkflowContext)!;
    const isSecurityAssetTxn = asset.Type === AssetTypes.ASSET;
    const user = useAppSelector(getUser);

    const declineReasonCode = selectedPledge.DeclineReason ? Number(selectedPledge.DeclineReason) : undefined;
    const declineReason = declineReasonCode && (
        DeclineReasons.find((reason: DeclineReason) => (
            reason.numericCode === declineReasonCode ||
            reason.code === String.fromCharCode(declineReasonCode)
        )) || {}
    ).reason || '';

    const menuOptions = useMemo(() => {
        const options: PledgeMenuOption[] = [
            {
                title: 'RELEASE',
                id: 'release'
            },
            {
                title: 'RECLAIM',
                id: 'reclaim'
            }
        ];

        if (userPledgorAccount && (userPledgorAccount.ClientRoleId !== ClientWalletRoleEnum.VIEW_ONLY)) {
            options.unshift(
                {
                    title: 'ADD ON',
                    id: 'addOn'
                }
            );
        }

        return options;
    }, [ selectedPledge, user, userPledgorAccount ]);

    const handleMenuSelect = (option: ModalScreen) => {
        changeModalScreen(option);

        if ([ 'approve', 'decline', 'cancel' ].includes(option)) {
            const transaction = selectedPledge.PledgeTransaction?.[0];
            if (transaction) {
                dispatchPledgeContextWorkflowAction({
                    type: 'SET_SELECTED_PLEDGE_TRANSACTION',
                    payload: transaction
                });
            }
        }
    };

    const showApproverActions = !user.IsAdmin && userPledgorAccount && userPledgorAccount.ClientRoleId !== ClientWalletRoleEnum.VIEW_ONLY;
    const isApprover = userPledgorAccount && userPledgorAccount.ClientRoleId === ClientWalletRoleEnum.APPROVER;
    const isRequester = userPledgorAccount && user.UserId === selectedPledge.PledgeTransaction?.[0]?.PledgeTransactionRequesterId;
    const showMenuActions = !user.IsAdmin && userPledgeeAccount && userPledgeeAccount.ClientRoleId !== ClientWalletRoleEnum.VIEW_ONLY;

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                className={classes.headerContainer}
                container
                direction='row'
                item
                justifyContent='space-between'
                spacing={0}
                wrap='nowrap'
                xs={12}
            >
                <Grid
                    container
                    item
                >
                    <Typography
                        className={classes.headerText}
                        variant='h4'
                    >
                        <strong>POST DETAILS</strong>
                    </Typography>
                    <Typography
                        className={classes.headerText}
                        variant='h4'
                    >
                        |
                    </Typography>
                    <Typography
                        className={classes.headerText}
                        id='transactionDetails_auditDateTime_value'
                        variant='h4'
                    >
                        {formatEpochDate(selectedPledge.AuditTime)}
                    </Typography>
                </Grid>
                <Grid
                    className={classes.buttonsContainer}
                    container
                    justifyContent='flex-end'
                >
                    {showApproverActions && selectedPledge.Status === TransactionStatusCode.PENDING_APPROVAL &&
                        (
                            isRequester ?
                                <Grid
                                    className={classes.approvalButtonContainer}
                                    direction='row'
                                    item
                                    justifyContent='flex-end'
                                    spacing={0}
                                    wrap='nowrap'
                                >
                                    <ApprovalButton
                                        id='transactionDetails_cancelTransaction_button'
                                        onClick={() => {
                                            handleMenuSelect('cancel');
                                        }}
                                        type='cancel'
                                    />
                                </Grid>
                                : isApprover &&
                                    <Grid
                                        className={classes.approvalButtonContainer}
                                        container
                                        direction='row'
                                        item
                                        justifyContent='flex-end'
                                        spacing={0}
                                        wrap='nowrap'
                                    >
                                        <ApprovalButton
                                            id='transactionDetails_declineTransaction_button'
                                            onClick={() => {
                                                    handleMenuSelect('decline');
                                                }}
                                            type='decline'
                                        />
                                        <ApprovalButton
                                            id='transactionDetails_approveTransaction_button'
                                            onClick={() => {
                                                    handleMenuSelect('approve');
                                                }}
                                            type='approve'
                                        />
                                    </Grid>
                        )}
                    {(selectedPledge.Status === TransactionStatusCode.CANCELED ||
                        selectedPledge.Status === TransactionStatusCode.DECLINED) && (
                        <Grid
                            className={classes.approvalButtonContainer}
                            direction='row'
                            item
                            justifyContent='flex-end'
                            spacing={0}
                            wrap='nowrap'
                        >
                            <ApprovalButton
                                id='transactionDetails_transactionDeclined_tag'
                                onClick={() => {
                                    return;
                                }}
                                type={selectedPledge.Status === TransactionStatusCode.DECLINED ? 'declined' : 'canceled'}
                            />
                        </Grid>
                    )}
                    {(showApproverActions || showMenuActions) && selectedPledge.Status === TransactionStatusCode.ACTIVE &&
                        <PledgeActionMenu
                            onSelect={(option) => handleMenuSelect(option.id)}
                            options={menuOptions}
                        />}
                </Grid>
            </Grid>
            <PledgeHeaderSectionDetails
                asset={asset}
                currentRole={currentRole}
                currentUser={currentUser}
                declineReason={declineReason}
                isSecurityAssetTxn={isSecurityAssetTxn}
                selectedPledge={selectedPledge}
            />
        </Grid>
    );
}